import * as XLSX from 'xlsx';

import type { FieldInput } from './fieldMetadata/fieldConfig';
import type { DataMap, ParseError } from './utils/parseFileUtils';

type ExcelRawCell = number | string | boolean | Date | undefined;
type DateParts = { y: number; m: number; d: number };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type ExcelParseResult = {
  dataMaps: DataMap[];
  errors: ParseError[];
};

export function parseExcel(buffer: ArrayBuffer, sheetIndex = 0): ExcelParseResult {
  try {
    const workbook = readWorkbook(buffer);
    const worksheet = readWorksheet(workbook, sheetIndex);
    if (!worksheet) {
      return {
        dataMaps: [],
        errors: [{ type: 'MissingWorksheet', message: `Worksheet ${sheetIndex + 1} not found` }],
      };
    }

    const dataMaps = worksheetToDataMaps(worksheet);
    if (dataMaps.length === 0) {
      return {
        dataMaps: [],
        errors: [
          {
            type: 'EmptyWorksheet',
            message: 'Worksheet does not contain any data row',
          },
        ],
      };
    }

    return { dataMaps, errors: [] };
  } catch {
    return {
      dataMaps: [],
      errors: [
        {
          type: 'InvalidWorkbook',
          message: 'Failed to read workbook',
        },
      ],
    };
  }
}

function readWorkbook(buffer: ArrayBuffer): XLSX.WorkBook {
  return XLSX.read(buffer, { cellDates: false, cellNF: true });
}

function readWorksheet(workbook: XLSX.WorkBook, sheetIndex = 0): XLSX.WorkSheet | undefined {
  const sheetName = workbook.SheetNames[sheetIndex];
  if (!sheetName) {
    return undefined;
  }

  return workbook.Sheets[sheetName];
}

// Excel date-only cells can shift when parsed as JS Date values
// So we must read raw cell values + number formats, then detect and parse Excel date cells
function worksheetToDataMaps(worksheet: XLSX.WorkSheet): DataMap[] {
  const rows = XLSX.utils.sheet_to_json<ExcelRawCell[]>(worksheet, {
    header: 1,
    raw: true,
    defval: undefined,
    blankrows: true,
  });

  const [headerRow, ...dataRows] = rows;
  if (!headerRow || headerRow.length === 0) {
    return [];
  }

  const headers = headerRow.map(normalizeExcelHeader);

  return dataRows
    .map((_, rowOffset) => {
      const rowIndex = rowOffset + 1;
      const entries = headers
        .map((key, columnIndex) => {
          if (!key) {
            return undefined;
          }

          const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
          const cell = worksheet[address] as XLSX.CellObject | undefined;
          const value = normalizeExcelCell(cell);
          if (value === undefined) {
            return undefined;
          }

          return [key, value] as [string, FieldInput];
        })
        .filter((entry): entry is [string, FieldInput] => entry !== undefined);

      return Object.fromEntries(entries);
    })
    .filter(dataMap => Object.keys(dataMap).length > 0);
}

function normalizeExcelHeader(cell: ExcelRawCell): string {
  return String(cell ?? '')
    .trim()
    .toLowerCase();
}

function normalizeExcelCell(cell: XLSX.CellObject | undefined): FieldInput {
  if (!cell) {
    return undefined;
  }

  if (isExcelDateCell(cell)) {
    return formatExcelDate(cell);
  }

  if (typeof cell.v === 'string') {
    return cell.v.trim();
  }

  return cell.v as FieldInput;
}

function isExcelDateCell(cell: XLSX.CellObject): boolean {
  if (cell.t === 'd') {
    return true;
  }

  if (cell.t !== 'n' || typeof cell.v !== 'number') {
    return false;
  }

  return typeof cell.z === 'string' && isExcelDateFormat(cell.z);
}

function formatExcelDate(cell: XLSX.CellObject): FieldInput {
  if (cell.v instanceof Date) {
    return toLocalDate({
      y: cell.v.getUTCFullYear(),
      m: cell.v.getUTCMonth() + 1,
      d: cell.v.getUTCDate(),
    });
  }

  if (typeof cell.v !== 'number') {
    return String(cell.v ?? '').trim();
  }

  const parsedDate = parseExcelDate(cell.v);
  if (!parsedDate) {
    return String(cell.v);
  }

  return toLocalDate(parsedDate);
}

export function isExcelDateFormat(format: string): boolean {
  const cleaned = format.replaceAll(/"[^"]*"/g, '').replaceAll(/\[[^\]]*\]/g, '');
  return /[ymd]/i.test(cleaned) || /[hs]/i.test(cleaned);
}

function parseExcelDate(serial: number): DateParts | undefined {
  if (!Number.isFinite(serial)) {
    return undefined;
  }

  const wholeDays = Math.floor(serial);
  let adjustedDays = wholeDays;

  if (wholeDays >= 60) {
    adjustedDays -= 1;
  }

  const epoch = Date.UTC(1899, 11, 31);
  const date = new Date(epoch + adjustedDays * MS_PER_DAY);

  return {
    y: date.getUTCFullYear(),
    m: date.getUTCMonth() + 1,
    d: date.getUTCDate(),
  };
}

function toLocalDate(date: DateParts): Date {
  return new Date(date.y, date.m - 1, date.d);
}
